import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import moment from "moment";
import { Input } from "semantic-ui-react";
import {
  datePickerDatePattern,
  momentDatePattern,
} from "shared/utils/dateTimeFormatting";
import styles from "./DateInput.module.scss";

const valuePattern = "YYYY-MM-DD";
const latestAllowedDate = "9999-12-31";
const datePickerClickInsideClass = "react-datepicker-ignore-onclickoutside";

const toDate = (value: string | undefined): Date | undefined => {
  const date = moment(value, valuePattern, true);
  return date.isValid() ? date.toDate() : undefined;
};

const toValue = (date: Date | null): string =>
  date ? moment(date).format(valuePattern) : "";

export type DateInputHandle = {
  focus: () => void;
};

type DateInputProps = {
  value: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  id?: string;
  name?: string;
  "aria-label"?: string;
  min?: string;
  max?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  className?: string;
};

const DateInput = forwardRef<DateInputHandle, DateInputProps>((props, ref) => {
  const datePicker = useRef<DatePicker>(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  useImperativeHandle(ref, () => ({
    focus: () => datePicker.current?.setFocus(),
  }));

  return (
    <div className={styles.container}>
      <DatePicker
        ref={datePicker}
        selected={toDate(props.value)}
        onChange={(date: Date | null) => props.onChange?.(toValue(date))}
        onBlur={props.onBlur}
        onCalendarOpen={() => setIsCalendarOpen(true)}
        onCalendarClose={() => setIsCalendarOpen(false)}
        minDate={toDate(props.min)}
        maxDate={toDate(props.max || latestAllowedDate)}
        dateFormat={datePickerDatePattern}
        strictParsing
        placeholderText={momentDatePattern}
        calendarStartDay={1}
        id={props.id}
        name={props.name}
        required={props.required}
        disabled={props.disabled}
        autoComplete="off"
        popperClassName={styles.popper}
        popperPlacement="bottom-start"
        showPopperArrow={false}
        popperProps={{ strategy: "fixed" }}
        customInput={
          <Input
            icon="calendar alternate outline"
            className={props.className}
            error={props.error}
            aria-label={props["aria-label"]}
            input={{
              className: isCalendarOpen ? datePickerClickInsideClass : undefined,
            }}
          />
        }
      />
    </div>
  );
});

export default DateInput;
